import { Col, List, Spin } from "antd";
import { isArray } from "core/utils";
import { useCallback } from "react";
import { v4 as uuidv4 } from "uuid";

import { canAccess } from "../../core/router/auth/permissions";
import { ResourceName } from "../../core/router/config/ResourceName";
import { AddButton } from "../FeatureButtons/AddButton";
import { EditableListHeader } from "./components/EditableListHeader";
import { EditableListItem } from "./components/EditableListItem";
import { ButtonBar } from "./styles/Styles";
import { INDEX_PROP } from "./types/Constants";
import type { EditableItem } from "./types/EditableItem";
import type { EditableListProps } from "./types/EditableListProps";
import { EditableListRemoveStrategy } from "./types/EditableListRemoveStrategy";

export const EditableList = <T extends EditableItem>(props: EditableListProps<T>) => {
    const { name, value, onChange, columns, addItems = true, removeItems = EditableListRemoveStrategy.ALWAYS, isFetching } = props;

    const onChangeCallback = useCallback(
        (item: T, index?: string) => {
            if (!onChange || !value || !isArray(value)) {
                return;
            }
            const getItemIndexValue = () => (item ? item[INDEX_PROP] : index);
            const getElementIndexValue = (element: T) => element[INDEX_PROP];
            const indexDoesntMatch = (element: T) => {
                return getElementIndexValue(element) !== getItemIndexValue();
            };

            let updatedElements;
            if (item === null) {
                updatedElements = value.filter(indexDoesntMatch);
            } else {
                updatedElements = value.map((element) => (indexDoesntMatch(element) ? element : item));
            }

            onChange(updatedElements);
        },
        [onChange, value],
    );

    const onClickAddCallback = () => {
        if (!onChange || !value || !isArray(value)) {
            return;
        }

        const hasElementPendingToBeSaved = !!value.find((element) => element.isEditing && element.isNew);
        if (hasElementPendingToBeSaved) {
            return;
        }

        const newElement = { index: uuidv4(), isEditing: true, isNew: true } as T;
        const newValue = [newElement, ...value];
        onChange(newValue);
    };

    const renderItem = useCallback(
        (item: T) => (
            <EditableListItem<T>
                {...props}
                value={item}
                columns={columns}
                canRemove={
                    removeItems === EditableListRemoveStrategy.ALWAYS || (removeItems === EditableListRemoveStrategy.ONLY_NEW && item.isNew)
                }
                canEdit={canAccess(ResourceName.APP_CONFIGURATIONS, "canWrite")}
                onChange={(newItemValue, index) => onChangeCallback(newItemValue, index)}
            />
        ),
        [props, columns, onChangeCallback, removeItems],
    );

    return (
        <Spin spinning={isFetching}>
            {addItems && (
                <ButtonBar>
                    <Col>
                        <AddButton callback={onClickAddCallback} name={`${name}_add_button`} />
                    </Col>
                </ButtonBar>
            )}
            {columns && value && isArray(value) && (
                <List
                    key={name}
                    bordered
                    header={<EditableListHeader columns={columns} />}
                    dataSource={value}
                    renderItem={renderItem}
                    grid={{ gutter: 0, column: 1 }} // This sets the content 100% width enabling the use of Ant Grid
                    pagination={{
                        pageSize: 10,
                    }}
                />
            )}
        </Spin>
    );
};
